import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';

import themed from 'webapp/decorators/themed';
import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import FormInputComponent from 'webapp/components/form/input';

interface Args {
	onChange: (value: number) => void;
	value: number | string | null;
	min: number;
	max?: number; // no maximum when not set
	maxlength: number;
	name?: string;
	getErrorText?: (type: ArgumentType, value: number | undefined) => string;
	showWarningOnMaxChange?: boolean;
	loseFocusOnEnter?: boolean;
	isDisabled?: boolean;
	theme?: string;
	size?: string;
}

type ArgumentType = 'min' | 'max';

@themed()
export default class InputIntegerComponent<T extends Args> extends Component<T> {
	@service declare infolayer: Service.Infolayer;

	@tracked _value: string | number;

	node: HTMLInputElement | null = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this._value = this.args.value ?? '';

		this.checkMinMax(false);
	}

	get value() {
		return this._value;
	}

	set value(v) {
		// since we sometimes want to override this change, we need an extra setter here

		const isEmpty = !v; // is string, so this flags '' but not '0'

		this._value = v;

		if (isEmpty) {
			// do nothing, allow input to stay empty until focusOut
			// on focusOut, if still empty, we'll revert it
		} else {
			const node = this.node;

			if (node) {
				const value = +node.value;

				if (value && value < this.args.min) {
					// do not revert on min value since maybe we're just editing first digit
					// focusOut will take care of that, if necessary
					return;
				}

				const cleanValue = this.cleanupValue(value);

				this.args.onChange(cleanValue);

				if (cleanValue !== value) {
					node.value = cleanValue.toString();
					this._value = cleanValue;
				}
			}
		}
	}

	@action
	onFocusOut(event: Event) {
		const inputNode = event.target as HTMLInputElement,
			isEmpty = !inputNode.value;

		if (isEmpty) {
			// reverts empty input to the minimum value
			const numericValue = this.cleanupValue(0);
			this.triggerChange(numericValue);
			inputNode.value = numericValue.toString();
		} else {
			this.checkMinMax(true);
		}
	}

	@action
	updateValue() {
		if (String(this.value) !== String(this.args.value)) {
			this._value = this.args.value ?? '';
		}
	}

	triggerChange(value: number) {
		this.args.onChange(value);
		this._value = value;
	}

	@action
	checkMinMaxOutsideChange() {
		// on external min/max change - option to not show error showWarningOnMaxChange
		this.checkMinMax(!!this.args.showWarningOnMaxChange);
	}

	checkMinMax(showWarning: boolean) {
		const value = +this.value,
			cleanValue = this.cleanupValue(value, showWarning);

		if (cleanValue !== value) {
			next(() => {
				this.triggerChange(cleanValue);
			});
		}
	}

	@action
	setupView(node: HTMLElement) {
		this.node = node as HTMLInputElement;
	}

	shortenValue(value: number): number {
		return +value.toString().substring(0, this.args.maxlength);
	}

	cleanupValue(value: number, showWarning = true) {
		let cleanValue;

		if (typeof value === 'number') {
			if (value.toString().length > this.args.maxlength) {
				value = this.shortenValue(value);
			}

			if (this.args.max !== undefined && value > this.args.max) {
				if (showWarning) {
					this.infolayer.error(this.getErrorText('max'));
				}
				cleanValue = this.args.max;
			} else if (value < this.args.min) {
				if (showWarning) {
					this.infolayer.error(this.getErrorText('min'));
				}
				cleanValue = this.args.min;
			} else {
				cleanValue = value;
			}
		} else {
			cleanValue = this.args.min;
		}

		return cleanValue;
	}

	getErrorText(type: ArgumentType) {
		const value = this.args[type];

		let text = '';
		if (this.args.getErrorText) {
			text = this.args.getErrorText(type, value);
		}
		if (!text) {
			const typeText = type === 'max' ? 'Maximum' : 'Minimum';
			text = `${typeText} value allowed is ${value}.`;
		}

		return text;
	}
	<template>
		<FormInputComponent
			name={{@name}}
			@type="number"
			@value={{this.value}}
			@focusOut={{this.onFocusOut}}
			max={{@max}}
			disabled={{@isDisabled}}
			@theme={{@theme}}
			@size={{@size}}
			@loseFocusOnEnter={{@loseFocusOnEnter}}
			{{didInsert this.setupView}}
			{{didUpdate this.checkMinMaxOutsideChange @min @max}}
			{{didUpdate this.updateValue @value}}
		/>
	</template>
}
